/**
 * Model channel -> organism channel, and the ZIP-scoped rows (locals, in-market RSNs) built from it.
 * Pure and import-light: the resolver uses it on the server and the lineup page in the browser.
 */

import type {
  LineupChannel as OrganismChannel,
  Membership as OrganismMembership
} from "../../directv-design-system/organisms/channel-lineup/channel-lineup.types";
import type { LineupMarketRows } from "../../directv-design-system/organisms/channel-lineup/lineup-market.shared";
import { stronger, type LineupChannel as ModelChannel } from "./lineup-model";

/** Model channel -> organism channel. A new object with a new `packages` map. */
export function toOrganismChannel(
  channel: ModelChannel,
  columnCodes: readonly string[] = []
): OrganismChannel {
  return {
    id: channel.id,
    name: channel.name,
    number: channel.number,
    callSign: channel.callSign,
    logoUrl: channel.logoUrl,
    categories: [...channel.categories],
    kind: channel.kind,
    // In-market RSNs only, and absent on everything else — GR5's tier legal note and GR4's team
    // badge have no other source.
    tier: channel.tier,
    teams: channel.teams ? [...channel.teams] : undefined,
    myFree: channel.myFree,
    fastChannel: channel.fastChannel,
    packages: withColumnAddonCells(channel, columnCodes)
  };
}

/**
 * GR2 — the channel's membership map, with its add-on reach projected onto the authored signature
 * columns. This is the fix for the missing `$` and `★`, and the owner's two examples are what it
 * reproduces.
 */
function withColumnAddonCells(
  channel: ModelChannel,
  columnCodes: readonly string[]
): Record<string, OrganismMembership> {
  const packages: Record<string, OrganismMembership> = { ...channel.packages };
  if (channel.kind === "rsn") {
    return withRsnColumnCells(packages, channel.beamPackageNames, columnCodes);
  }
  const reach = channel.addonReach;
  // An out-of-market RSN is sold like a national (Sports Pack promo), so it reaches the columns too.
  if (!reach || (channel.kind !== "national" && channel.kind !== "rsn-oom")) {
    return packages;
  }
  for (const code of columnCodes) {
    packages[code] = stronger(packages[code], reach);
  }
  return packages;
}

/** v2's `RSN_DEFAULT_PACKAGES` (ATT_TV_CHOICE/PREMIER/ULTIMATE) as plan names. */
const RSN_DEFAULT_PLAN_NAMES = ["CHOICE", "PREMIER", "ULTIMATE"] as const;

/** A plan name as beam and a billing code both spell it: upper-case, no ™/®. */
function planNameKey(value: string): string {
  return value.replace(/[™®]/g, "").trim().toUpperCase();
}

/** `BASE-CHOICE-201811` -> `CHOICE`: the code's own plan segment, so the match
 *  stays inside billing codes and never reaches package-config ids (§1). */
function columnPlanSegment(code: string): string {
  const parts = code.split("-");
  return parts.length >= 3 ? planNameKey(parts.slice(1, -1).join("-")) : "";
}

/**
 * GR2 — an in-market RSN's ✓ cells. Beam states membership as plan display names ("choice",
 * "ultimate"); each signature column whose code segment equals one of them is `included`.
 */
export function withRsnColumnCells(
  packages: Record<string, OrganismMembership>,
  beamPackageNames: readonly string[] | undefined,
  columnCodes: readonly string[]
): Record<string, OrganismMembership> {
  const given = (beamPackageNames ?? []).map(planNameKey).filter(Boolean);
  // A deliberate default, not v2 parity: v2 ticks from the raw names (ChnlListingPage.tsx:560) and drops
  // a row with no ✓ (:577-584); its default list only feeds pack membership (:566). An RSN with no
  // packages is beam's data issue; this keeps the row visible.
  const names = new Set(given.length > 0 ? given : RSN_DEFAULT_PLAN_NAMES);
  for (const code of columnCodes) {
    if (names.has(columnPlanSegment(code))) {
      packages[code] = stronger(packages[code], "included");
    }
  }
  return packages;
}

/**
 * This market's rows for one lineup. Every local gets a row; an RSN only with a ✓ in a signature
 * column, as v2 drops the rest (ChnlListingPageV2/ChnlListingPage.tsx:577-584).
 */
export function marketRowsFor(
  locals: readonly ModelChannel[],
  rsns: readonly ModelChannel[],
  columnCodes: readonly string[]
): LineupMarketRows {
  const localChannels = locals.map(channel => toOrganismChannel(channel, columnCodes));
  const allRsns = rsns.map(channel => toOrganismChannel(channel, columnCodes));
  return {
    localChannels,
    rsns: allRsns.filter(channel => columnCodes.some(code => channel.packages?.[code] === "included")),
    packChannels: [...localChannels, ...allRsns]
  };
}
